import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TableModule } from 'primeng/table';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToasterService } from '@ui-nuvertos/auth';
import {
  RelatedDocumentsService,
  RelatedDocumentsModule,
  RelatedDocument,
} from '@ui-nuvertos/shared/services/related-documents/related-documents.service';

/**
 * Shared "Related Documents" table used by every document detail screen
 * (PO, Purchase Invoice, Debit Note, Sales Order, Sales Invoice, Credit Note).
 *
 * The host only declares the tab and passes `module` + `id`; this component owns
 * the fetch, its subscription lifecycle, columns, badge colors, row navigation,
 * and date formatting — so behavior is identical across screens and there is no
 * per-host boilerplate to keep in sync.
 */
@Component({
  selector: 'lib-related-documents-tab',
  standalone: true,
  imports: [CommonModule, TableModule],
  templateUrl: './related-documents-tab.html',
})
export class RelatedDocumentsTab implements OnInit, OnDestroy {
  /** Which module the entity belongs to (drives the API + navigation). */
  @Input({ required: true }) module!: RelatedDocumentsModule;
  /** The entity id whose related documents to load. */
  @Input({ required: true }) id!: string | number;
  @Input() emptyMessage = 'No related documents found.';

  documents: RelatedDocument[] = [];
  loading = false;
  loadError = false;

  /** Route + query-param name for each document type, used for row navigation. */
  private static readonly NAVIGATION: Record<string, { route: string; param: string }> = {
    'Purchase Order':   { route: '/vendors/purchase-order',   param: 'poId' },
    'GRN':              { route: '/vendors/grn/create',       param: 'grnId' },
    'Purchase Invoice': { route: '/vendors/purchase-invoice', param: 'invoiceId' },
    'Debit Note':       { route: '/vendors/debit-note',       param: 'debitNoteId' },
    'Payment':          { route: '/vendors/vendor-payment',   param: 'billPaymentId' },
    'Sales Order':      { route: '/clients/sales-order',      param: 'soId' },
    'Sales Invoice':    { route: '/clients/client-invoice',   param: 'invoiceId' },
    'Credit Note':      { route: '/clients/credit-note',      param: 'id' },
    'Receipt':          { route: '/clients/client-payment',   param: 'billPaymentId' },
  };

  /** Tailwind badge classes per document type. */
  readonly badgeClasses: Record<string, string> = {
    'Purchase Order':   'bg-purple-100 text-purple-700',
    'GRN':              'bg-blue-100 text-blue-700',
    'Purchase Invoice': 'bg-green-100 text-green-700',
    'Debit Note':       'bg-amber-100 text-amber-700',
    'Payment':          'bg-teal-100 text-teal-700',
    'Sales Order':      'bg-purple-100 text-purple-700',
    'Sales Invoice':    'bg-green-100 text-green-700',
    'Credit Note':      'bg-amber-100 text-amber-700',
    'Receipt':          'bg-teal-100 text-teal-700',
  };

  private destroy$ = new Subject<void>();

  constructor(
    private relatedDocumentsService: RelatedDocumentsService,
    private router: Router,
    private toasterService: ToasterService
  ) {}

  ngOnInit(): void {
    if (this.id === null || this.id === undefined || this.id === '') {
      return;
    }
    this.loading = true;
    this.loadError = false;
    this.relatedDocumentsService
      .getRelatedDocuments(this.module, this.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.documents = res?.data?.related_documents ?? [];
          this.loading = false;
        },
        error: () => {
          this.documents = [];
          this.loading = false;
          this.loadError = true;
          this.toasterService.error('Error', 'Failed to load related documents');
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Whether a document type has a navigation target (controls clickability/styling). */
  isNavigable(doc: RelatedDocument): boolean {
    return !!RelatedDocumentsTab.NAVIGATION[doc.document_type];
  }

  navigateTo(doc: RelatedDocument): void {
    const nav = RelatedDocumentsTab.NAVIGATION[doc.document_type];
    if (!nav) {
      return;
    }
    this.router.navigate([nav.route], {
      queryParams: { [nav.param]: String(doc.document_id), mode: 'view' },
    });
  }
}
